import api from "@/config/api";
import type { RequestOptions } from "@/shared/types/api.types";
import { API_ENDPOINTS } from "@/constants/api-endpoints";
import type { PageResponseDTO } from "@/shared/types/pagination.types";
import type {
  RentalCreateRequestDTO,
  RentalPickupRequestDTO,
  RentalResponseDTO,
  RentalSearchParams,
  RentalSummaryParams,
  RentalSummaryResponseDTO,
  RentalUpdateRequestDTO,
} from "../types/rental.types";

/**
 * Cliente HTTP da feature de aluguéis.
 *
 * `search` consome `/rentals/search` (escopo USER + ADMIN).
 * `adminSearch` consome `/admin/rentals/search` (uso restrito a telas
 * admin-only). Demais métodos são acessíveis ao USER.
 */
export const rentalService = {
  search: async (
    params?: RentalSearchParams,
    options?: RequestOptions,
  ): Promise<PageResponseDTO<RentalResponseDTO>> => {
    const response = await api.get<PageResponseDTO<RentalResponseDTO>>(
      API_ENDPOINTS.RENTALS.SEARCH,
      { params, signal: options?.signal },
    );
    return response.data;
  },

  adminSearch: async (
    params?: RentalSearchParams,
    options?: RequestOptions,
  ): Promise<PageResponseDTO<RentalResponseDTO>> => {
    const response = await api.get<PageResponseDTO<RentalResponseDTO>>(
      API_ENDPOINTS.RENTALS_ADMIN.SEARCH,
      { params, signal: options?.signal },
    );
    return response.data;
  },

  getById: async (
    id: number,
    options?: RequestOptions,
  ): Promise<RentalResponseDTO> => {
    const response = await api.get<RentalResponseDTO>(
      API_ENDPOINTS.RENTALS.BY_ID(id),
      { signal: options?.signal },
    );
    return response.data;
  },

  summary: async (
    params: RentalSummaryParams,
  ): Promise<RentalSummaryResponseDTO> => {
    const response = await api.get<RentalSummaryResponseDTO>(
      API_ENDPOINTS.RENTALS.SUMMARY,
      { params },
    );
    return response.data;
  },

  update: async (
    id: number,
    data: RentalUpdateRequestDTO,
  ): Promise<RentalResponseDTO> => {
    const response = await api.put<RentalResponseDTO>(
      API_ENDPOINTS.RENTALS.BY_ID(id),
      data,
    );
    return response.data;
  },

  cancel: async (id: number): Promise<RentalResponseDTO> => {
    const response = await api.post<RentalResponseDTO>(
      API_ENDPOINTS.RENTALS.CANCEL(id),
    );
    return response.data;
  },

  create: async (data: RentalCreateRequestDTO): Promise<RentalResponseDTO> => {
    const response = await api.post<RentalResponseDTO>(
      API_ENDPOINTS.RENTALS.BASE,
      data,
    );
    return response.data;
  },

  pickup: async (
    id: number,
    data?: RentalPickupRequestDTO,
  ): Promise<RentalResponseDTO> => {
    const response = await api.post<RentalResponseDTO>(
      API_ENDPOINTS.RENTALS.PICKUP(id),
      data ?? {},
    );
    return response.data;
  },
};
